import React from 'react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Loader2, Sparkles, FileText, Megaphone, ExternalLink, CheckCircle2, Globe } from 'lucide-react';
import { LEVELS, LL, JD_STATUSES, POSTING_STATUSES, STATUS_STYLES } from './constants';

export function Section({label, body}){
  return (
    <div>
      <p className="text-[10px] font-semibold uppercase tracking-wider text-[#6B3FA0] mb-1">{label}</p>
      <p className="text-xs text-[#5A4D6E] whitespace-pre-line">{body}</p>
    </div>
  );
}

export function PosDialog({form,setForm,close,save,saving,positions,config}){
  const set = (k,v)=>setForm(p=>({...p,[k]:v}));
  const divisions = config?.divisions || [];
  return (
    <Dialog open onOpenChange={close}>
      <DialogContent className="sm:max-w-md bg-white max-h-[85vh] overflow-y-auto" data-testid="position-dialog">
        <DialogHeader><DialogTitle className="text-xl font-bold text-[#2D1B4E]">{form.id?'Edit':'Add'} Position</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <div><Label>Title</Label><Input value={form.title||''} onChange={e=>set('title',e.target.value)} className="border-[#DED8EB]" data-testid="pos-title"/></div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Level</Label>
              <select value={form.level||'dsp'} onChange={e=>set('level',e.target.value)} className="w-full border border-[#DED8EB] rounded-md px-3 py-2 text-sm" data-testid="pos-level">
                {LEVELS.map(l=><option key={l} value={l}>{LL[l]}</option>)}
              </select>
            </div>
            <div>
              <Label>Service Line</Label>
              <select value={form.service_line||''} onChange={e=>set('service_line',e.target.value)} className="w-full border border-[#DED8EB] rounded-md px-3 py-2 text-sm" data-testid="pos-service-line">
                <option value="">All</option>
                <option value="day_program">Explore</option>
                <option value="employment">Employment</option>
                <option value="in_home">IHCS</option>
                <option value="private_pay">Private Pay</option>
              </select>
            </div>
          </div>
          <div>
            <Label>Division</Label>
            <select value={form.division_id||''} onChange={e=>set('division_id',e.target.value)} className="w-full border border-[#DED8EB] rounded-md px-3 py-2 text-sm" data-testid="pos-division">
              <option value="">— None —</option>
              {divisions.map(d=><option key={d.id} value={d.id}>{d.label}</option>)}
            </select>
            <p className="text-[10px] text-[#8E85A0] mt-1">Manage divisions under Staffing → Settings</p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Budgeted</Label><Input type="number" value={form.budgeted||0} onChange={e=>set('budgeted',parseInt(e.target.value)||0)} className="border-[#DED8EB]"/></div>
            <div><Label>Filled</Label><Input type="number" value={form.filled||0} onChange={e=>set('filled',parseInt(e.target.value)||0)} className="border-[#DED8EB]"/></div>
          </div>
          <div>
            <Label>Reports To</Label>
            <select value={form.reports_to||''} onChange={e=>set('reports_to',e.target.value)} className="w-full border border-[#DED8EB] rounded-md px-3 py-2 text-sm" data-testid="pos-reports-to">
              <option value="">None</option>
              {positions.filter(pp=>pp.id!==form.id).map(pp=><option key={pp.id} value={pp.id}>{pp.title}</option>)}
            </select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={close}>Cancel</Button>
          <Button onClick={save} disabled={saving||!form.title} className="bg-[#6B3FA0] hover:bg-[#5A2F8E] text-white" data-testid="pos-save-btn">{saving?'Saving...':'Save'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function JdDialog({form,setForm,close,save,saving,generating,generate,config}){
  const [section, setSection] = React.useState('details');
  const sections = [
    {k:'details', l:'Details'},
    {k:'content', l:'Content'},
    {k:'ai', l:'AI Assist'},
  ];
  const set = (k,v) => setForm(p=>({...p,[k]:v}));
  const bands = config?.salary_bands || [];
  const benefitsCatalog = config?.benefits || [];
  const divisions = config?.divisions || [];
  const currentBenefitsIds = form.benefits_ids || [];
  const toggleBenefit = (id) => {
    const has = currentBenefitsIds.includes(id);
    const next = has ? currentBenefitsIds.filter(x=>x!==id) : [...currentBenefitsIds, id];
    set('benefits_ids', next);
  };
  const onBandChange = (id) => {
    set('salary_band_id', id);
    if (id) {
      const b = bands.find(x=>x.id===id);
      if (b && !form.salary_range) set('salary_range', `$${b.min}-${b.max}/hr`);
    }
  };
  return (
    <Dialog open onOpenChange={close}>
      <DialogContent className="sm:max-w-2xl bg-white max-h-[88vh] overflow-hidden flex flex-col p-0" data-testid="jd-dialog">
        <DialogHeader className="px-6 pt-5 pb-3 border-b border-[#DED8EB]">
          <DialogTitle className="text-xl font-bold text-[#2D1B4E] flex items-center gap-2">
            <FileText className="h-5 w-5 text-[#6B3FA0]"/> {form.id?'Edit':'New'} Job Description
          </DialogTitle>
        </DialogHeader>
        <div className="flex items-center gap-1 px-6 pt-3 border-b border-[#DED8EB] bg-[#F7F4EF]">
          {sections.map(s=>(
            <button key={s.k} onClick={()=>setSection(s.k)}
              className={`px-3 py-2 text-sm font-medium rounded-t-md border-b-2 transition-all -mb-px ${
                section===s.k?'text-[#6B3FA0] border-[#6B3FA0] bg-white':'text-[#8E85A0] border-transparent hover:text-[#2D1B4E]'
              }`} data-testid={`jd-section-${s.k}`}>
              {s.l}
            </button>
          ))}
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3">
          {section==='details' && (
            <div className="space-y-3">
              <div>
                <Label>Title</Label>
                <Input value={form.title||''} onChange={e=>set('title',e.target.value)} className="border-[#DED8EB]" data-testid="jd-title"/>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Salary Band</Label>
                  <select value={form.salary_band_id||''} onChange={e=>onBandChange(e.target.value)} className="w-full border border-[#DED8EB] rounded-md px-3 py-2 text-sm" data-testid="jd-salary-band">
                    <option value="">— Custom / None —</option>
                    {bands.map(b=><option key={b.id} value={b.id}>{b.label} (${b.min}-${b.max}/hr)</option>)}
                  </select>
                </div>
                <div><Label>Salary Range (override)</Label><Input value={form.salary_range||''} onChange={e=>set('salary_range',e.target.value)} placeholder="$15-18/hr" className="border-[#DED8EB]" data-testid="jd-salary"/></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Service Line</Label>
                  <select value={form.service_line||''} onChange={e=>set('service_line',e.target.value)} className="w-full border border-[#DED8EB] rounded-md px-3 py-2 text-sm" data-testid="jd-service-line">
                    <option value="">All</option>
                    <option value="day_program">Explore</option>
                    <option value="employment">Employment</option>
                    <option value="in_home">IHCS</option>
                    <option value="private_pay">Private Pay</option>
                  </select>
                </div>
                <div>
                  <Label>Division</Label>
                  <select value={form.division_id||''} onChange={e=>set('division_id',e.target.value)} className="w-full border border-[#DED8EB] rounded-md px-3 py-2 text-sm" data-testid="jd-division">
                    <option value="">— None —</option>
                    {divisions.map(d=><option key={d.id} value={d.id}>{d.label}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Status</Label>
                  <select value={form.status||'draft'} onChange={e=>set('status',e.target.value)} className="w-full border border-[#DED8EB] rounded-md px-3 py-2 text-sm" data-testid="jd-status">
                    {JD_STATUSES.map(s=><option key={s} value={s}>{s.charAt(0).toUpperCase()+s.slice(1)}</option>)}
                  </select>
                </div>
                <div>
                  <Label>Employment Type</Label>
                  <select value={form.employment_type||'full_time'} onChange={e=>set('employment_type',e.target.value)} className="w-full border border-[#DED8EB] rounded-md px-3 py-2 text-sm" data-testid="jd-employment-type">
                    <option value="full_time">Full-Time</option>
                    <option value="part_time">Part-Time</option>
                    <option value="temp">Temporary</option>
                    <option value="contract">Contract</option>
                  </select>
                </div>
              </div>
              <div>
                <Label>Certifications</Label>
                <Input value={form.certifications||''} onChange={e=>set('certifications',e.target.value)} placeholder="CPR, First Aid, PA DL" className="border-[#DED8EB]" data-testid="jd-certs"/>
              </div>
              <div><Label>Summary</Label><Textarea value={form.summary||''} onChange={e=>set('summary',e.target.value)} className="border-[#DED8EB] min-h-[70px] text-sm" placeholder="Brief overview of the role..." data-testid="jd-summary"/></div>
            </div>
          )}
          {section==='content' && (
            <div className="space-y-3">
              <div><Label>Essential Duties</Label><Textarea value={form.duties||''} onChange={e=>set('duties',e.target.value)} className="border-[#DED8EB] min-h-[140px] text-sm" placeholder="- Provide direct support..." data-testid="jd-duties"/></div>
              <div><Label>Qualifications</Label><Textarea value={form.qualifications||''} onChange={e=>set('qualifications',e.target.value)} className="border-[#DED8EB] min-h-[100px] text-sm" placeholder="- HS diploma, 18+, valid license..." data-testid="jd-qualifications"/></div>
              <div><Label>Physical Requirements</Label><Textarea value={form.physical_reqs||''} onChange={e=>set('physical_reqs',e.target.value)} className="border-[#DED8EB] min-h-[80px] text-sm" placeholder="Ability to lift 25-50 lbs..." data-testid="jd-physical"/></div>
              <div>
                <Label>Benefits</Label>
                {benefitsCatalog.length > 0 ? (
                  <div className="grid grid-cols-2 gap-2 mt-1" data-testid="jd-benefits-checkboxes">
                    {benefitsCatalog.map(b=>(
                      <label key={b.id} className={`flex items-center gap-2 px-3 py-2 rounded-md border cursor-pointer transition-all text-sm ${currentBenefitsIds.includes(b.id)?'border-[#6B3FA0] bg-[#EDE7F6] text-[#2D1B4E]':'border-[#DED8EB] text-[#5A4D6E] hover:border-[#9B7ED8]'}`} data-testid={`jd-benefit-${b.id}`}>
                        <input type="checkbox" checked={currentBenefitsIds.includes(b.id)} onChange={()=>toggleBenefit(b.id)} className="accent-[#6B3FA0]"/>
                        {b.label}
                      </label>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-[#8E85A0] mt-1">No benefits configured. Go to <strong>Staffing → Settings</strong> to add.</p>
                )}
                <Textarea value={form.benefits||''} onChange={e=>set('benefits',e.target.value)} className="border-[#DED8EB] min-h-[50px] text-sm mt-2" placeholder="Optional: additional benefits notes..." data-testid="jd-benefits"/>
              </div>
            </div>
          )}
          {section==='ai' && (
            <div className="space-y-3" data-testid="ai-generator">
              <div className="p-4 rounded-lg bg-[#EDE7F6] border border-[#9B7ED8] space-y-3">
                <p className="text-sm font-semibold text-[#2D1B4E] flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-[#6B3FA0]"/> AI Draft Assistant
                </p>
                <p className="text-xs text-[#5A4D6E]">
                  {form.id ? 'Re-generate content from a fresh prompt. Existing values will be overwritten.' : 'Fill the prompt, then click Generate. All sections will be populated.'}
                </p>
                <div>
                  <Label>Position Title</Label>
                  <Input value={form.gen_title||form.title||''} onChange={e=>set('gen_title',e.target.value)} placeholder="e.g. Lead Direct Support Professional" className="border-[#9B7ED8] bg-white" data-testid="gen-title"/>
                </div>
                <div>
                  <Label>Level</Label>
                  <select value={form.gen_level||''} onChange={e=>set('gen_level',e.target.value)} className="w-full border border-[#9B7ED8] bg-white rounded-md px-3 py-2 text-sm" data-testid="gen-level">
                    <option value="">-- Select --</option>
                    {LEVELS.map(l=><option key={l} value={l}>{LL[l]}</option>)}
                  </select>
                </div>
                <div>
                  <Label>Key Duties / Notes (optional)</Label>
                  <Textarea value={form.gen_duties||''} onChange={e=>set('gen_duties',e.target.value)} placeholder="Anything specific: overnight shift, group home, employment coaching..." className="border-[#9B7ED8] bg-white min-h-[70px]" data-testid="gen-duties"/>
                </div>
                <Button onClick={generate} disabled={generating||!(form.gen_title||form.title)} className="bg-[#6B3FA0] hover:bg-[#5A2F8E] text-white gap-2 w-full" data-testid="generate-btn">
                  {generating?<Loader2 className="h-4 w-4 animate-spin"/>:<Sparkles className="h-4 w-4"/>}
                  {generating?'Generating (~15s)...':'Generate & Fill All Sections'}
                </Button>
                <p className="text-[10px] text-[#8E85A0]">Powered by GPT-5.2. Review & edit in Details/Content tabs after generation.</p>
              </div>
              {form.content && (
                <div>
                  <Label>Full Content (optional, manually editable)</Label>
                  <Textarea value={form.content} onChange={e=>set('content',e.target.value)} className="border-[#DED8EB] min-h-[150px] text-sm font-mono"/>
                </div>
              )}
            </div>
          )}
        </div>
        <DialogFooter className="px-6 py-3 border-t border-[#DED8EB] bg-[#F7F4EF]">
          <Button variant="outline" onClick={close} data-testid="jd-cancel-btn">Cancel</Button>
          <Button onClick={save} disabled={saving||!form.title} className="bg-[#6B3FA0] hover:bg-[#5A2F8E] text-white gap-2" data-testid="jd-save-btn">
            {saving?<Loader2 className="h-4 w-4 animate-spin"/>:null}
            {saving?'Saving...':form.id?'Update':'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function PostDialog({form,setForm,close,save,saving,jds}){
  const linkedJd = form.job_description_id ? jds.find(j=>j.id===form.job_description_id) : null;

  const applyJdToPosting = (jd, { overwriteAll=false } = {}) => {
    if (!jd) return;
    const jdDescription = [jd.summary, jd.duties].filter(Boolean).join('\n\n');
    setForm(p => ({
      ...p,
      job_description_id: jd.id,
      title: (overwriteAll || !p.title) ? jd.title : p.title,
      description: (overwriteAll || !p.description) ? jdDescription : p.description,
      requirements: (overwriteAll || !p.requirements) ? (jd.qualifications || '') : p.requirements,
      salary_range: (overwriteAll || !p.salary_range) ? (jd.salary_range || '') : p.salary_range,
      service_line: (overwriteAll || !p.service_line) ? (jd.service_line || '') : p.service_line,
    }));
  };

  const onPickJd = (id) => {
    if (!id) { setForm(p => ({ ...p, job_description_id: '' })); return; }
    const jd = jds.find(j=>j.id===id);
    applyJdToPosting(jd);
    if (jd) toast.success(`Pulled in details from "${jd.title}"`);
  };

  return (
    <Dialog open onOpenChange={close}>
      <DialogContent className="sm:max-w-2xl bg-white max-h-[88vh] overflow-y-auto" data-testid="posting-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-[#2D1B4E] flex items-center gap-2">
            <Megaphone className="h-5 w-5 text-[#6B3FA0]"/> {form.id?'Edit':'New'} Posting
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="p-3 rounded-lg bg-[#EDE7F6] border border-[#9B7ED8] space-y-2">
            <Label className="text-[#2D1B4E] font-semibold flex items-center gap-2">
              <FileText className="h-4 w-4 text-[#6B3FA0]"/> Linked Job Description
            </Label>
            <select value={form.job_description_id||''} onChange={e=>onPickJd(e.target.value)}
              className="w-full border border-[#9B7ED8] bg-white rounded-md px-3 py-2 text-sm" data-testid="posting-jd-select">
              <option value="">— None (manual posting) —</option>
              {jds.map(j=><option key={j.id} value={j.id}>{j.title} ({j.status})</option>)}
            </select>
            {linkedJd && (
              <div className="text-xs text-[#5A4D6E] space-y-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge className={`text-[9px] ${STATUS_STYLES[linkedJd.status]||''}`}>JD {linkedJd.status}</Badge>
                  {linkedJd.salary_range && <span>{linkedJd.salary_range}</span>}
                  {linkedJd.certifications && <span>· Certs: {linkedJd.certifications}</span>}
                </div>
                {linkedJd.summary && <p className="line-clamp-2 text-[#5A4D6E]">{linkedJd.summary}</p>}
                <Button size="sm" variant="outline" onClick={()=>applyJdToPosting(linkedJd,{overwriteAll:true})}
                  className="border-[#9B7ED8] text-[#6B3FA0] h-7 text-xs mt-1" data-testid="resync-jd-btn">
                  Re-sync all fields from JD
                </Button>
              </div>
            )}
          </div>

          <div><Label>Title</Label><Input value={form.title||''} onChange={e=>setForm(p=>({...p,title:e.target.value}))} className="border-[#DED8EB]" data-testid="post-title"/></div>
          <div>
            <Label>Description</Label>
            <Textarea value={form.description||''} onChange={e=>setForm(p=>({...p,description:e.target.value}))}
              className="border-[#DED8EB] min-h-[100px] text-sm" placeholder="What will they do? Who's the ideal candidate?" data-testid="post-description"/>
          </div>
          <div>
            <Label>Requirements</Label>
            <Textarea value={form.requirements||''} onChange={e=>setForm(p=>({...p,requirements:e.target.value}))}
              className="border-[#DED8EB] min-h-[70px] text-sm" placeholder="- HS diploma, 18+, PA driver's license..." data-testid="post-requirements"/>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Salary Range</Label><Input value={form.salary_range||''} onChange={e=>setForm(p=>({...p,salary_range:e.target.value}))} placeholder="$15-18/hr" className="border-[#DED8EB]" data-testid="post-salary"/></div>
            <div><Label>Deadline</Label><Input type="date" value={form.deadline||''} onChange={e=>setForm(p=>({...p,deadline:e.target.value}))} className="border-[#DED8EB]" data-testid="post-deadline"/></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Service Line</Label>
              <select value={form.service_line||''} onChange={e=>setForm(p=>({...p,service_line:e.target.value}))} className="w-full border border-[#DED8EB] rounded-md px-3 py-2 text-sm" data-testid="post-service-line">
                <option value="">Any</option>
                <option value="day_program">Explore</option>
                <option value="employment">Employment</option>
                <option value="in_home">IHCS</option>
                <option value="private_pay">Private Pay</option>
              </select>
            </div>
            <div>
              <Label>Status</Label>
              <select value={form.status||'draft'} onChange={e=>setForm(p=>({...p,status:e.target.value}))} className="w-full border border-[#DED8EB] rounded-md px-3 py-2 text-sm" data-testid="post-status">
                {POSTING_STATUSES.map(s=><option key={s} value={s}>{s.charAt(0).toUpperCase()+s.slice(1)}</option>)}
              </select>
            </div>
          </div>
          <div>
            <Label>Employment Type</Label>
            <select value={form.employment_type||'full_time'} onChange={e=>setForm(p=>({...p,employment_type:e.target.value}))} className="w-full border border-[#DED8EB] rounded-md px-3 py-2 text-sm" data-testid="post-employment-type">
              <option value="full_time">Full-Time</option>
              <option value="part_time">Part-Time</option>
              <option value="temp">Temporary</option>
              <option value="contract">Contract</option>
            </select>
          </div>
          <label className="flex items-start gap-3 p-3 rounded-lg border border-[#80CBC4] bg-[#E0F2F1] cursor-pointer hover:bg-[#B2DFDB] transition-colors" data-testid="post-public-toggle">
            <input
              type="checkbox"
              checked={!!form.is_public}
              onChange={e=>setForm(p=>({...p,is_public:e.target.checked}))}
              className="mt-0.5 h-4 w-4 accent-[#00695C]"
              data-testid="post-public-checkbox"
            />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <Globe className="h-4 w-4 text-[#00695C]"/>
                <span className="text-sm font-semibold text-[#00474A]">Show on public Career Portal</span>
              </div>
              <p className="text-xs text-[#00695C] mt-0.5">When enabled, this posting appears at <span className="font-mono">/careers</span> and accepts applications from non-staff visitors. Status must be <strong>open</strong> or <strong>interviewing</strong> to be visible.</p>
            </div>
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={close}>Cancel</Button>
          <Button onClick={save} disabled={saving||!form.title} className="bg-[#6B3FA0] hover:bg-[#5A2F8E] text-white" data-testid="posting-save-btn">
            {saving?'Saving...':form.id?'Update':'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function AppsDialog({apps,close,updateApp}){
  return(
    <Dialog open onOpenChange={close}>
      <DialogContent className="sm:max-w-lg bg-white max-h-[85vh] overflow-y-auto" data-testid="apps-dialog">
        <DialogHeader><DialogTitle className="text-lg font-bold text-[#2D1B4E]">Applications</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          {apps.length===0 ? (
            <p className="text-sm text-[#8E85A0] text-center py-4">No applications yet</p>
          ) : apps.map(a => (
            <div key={a.id} className="p-3 rounded-lg border border-[#DED8EB] space-y-1">
              <div className="flex items-center justify-between flex-wrap gap-1">
                <span className="text-sm font-semibold text-[#2D1B4E]">{a.user_name}</span>
                <div className="flex items-center gap-1">
                  {a.source==='public' && <Badge className="text-[9px] bg-[#E0F2F1] text-[#00695C] gap-1"><Globe className="h-2.5 w-2.5"/>External</Badge>}
                  <Badge className={`text-[9px] ${STATUS_STYLES[a.status]||''}`}>{a.status}</Badge>
                </div>
              </div>
              <p className="text-xs text-[#8E85A0]">{a.user_email}{a.phone?` · ${a.phone}`:''}</p>
              {a.resume_url && <a href={a.resume_url} target="_blank" rel="noopener noreferrer" className="text-xs text-[#6B3FA0] hover:underline inline-flex items-center gap-1 font-semibold"><ExternalLink className="h-3 w-3"/>Resume / Profile</a>}
              {a.message && <p className="text-xs text-[#5A4D6E] whitespace-pre-wrap">"{a.message}"</p>}
              {a.experience && <p className="text-xs text-[#5A4D6E]"><strong>Experience:</strong> {a.experience}</p>}
              {a.certifications && <p className="text-xs text-[#5A4D6E]"><strong>Certs:</strong> {a.certifications}</p>}
              {a.availability && <p className="text-xs text-[#5A4D6E]"><strong>Availability:</strong> {a.availability}</p>}
              <div className="flex gap-1 mt-1">
                <Button size="sm" variant="outline" onClick={()=>updateApp(a.id,'reviewed')} className="h-6 text-[10px]">Review</Button>
                <Button size="sm" variant="outline" onClick={()=>updateApp(a.id,'accepted')} className="h-6 text-[10px] text-[#1B5E20] border-[#81C784]"><CheckCircle2 className="h-3 w-3 mr-0.5"/>Accept</Button>
                <Button size="sm" variant="outline" onClick={()=>updateApp(a.id,'rejected')} className="h-6 text-[10px] text-[#D9534F] border-red-300">Reject</Button>
              </div>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
